import { useMemo } from 'react'
import styled, { DefaultTheme } from 'styled-components'
import { BigNumber } from '@ethersproject/bignumber'
import { Box, Flex, FlexProps, Skeleton, Text } from '@pancakeswap/uikit'
import { useTranslation } from '@pancakeswap/localization'
import { BetPosition, NodeRound, Round } from 'state/types'
import { useConfig } from 'views/Predictions/context/ConfigProvider'
import { formatUsdv2, formatBnbv2, getRoundPosition, getPriceDifference } from '../../helpers'
import { formatBnb, formatUsd } from '../History/helpers'
import PositionTag from '../PositionTag'

// PrizePoolRow
interface PrizePoolRowProps extends FlexProps {
  totalAmount: NodeRound['totalAmount']
}

const getPrizePoolAmount = (totalAmount: PrizePoolRowProps['totalAmount'], displayedDecimals: number) => {
  if (!totalAmount) {
    return '0'
  }

  return formatBnbv2(totalAmount, displayedDecimals)
}

const Row = ({ children, ...props }) => {
  return (
    <Flex alignItems="center" justifyContent="space-between" {...props}>
      {children}
    </Flex>
  )
}

export const PrizePoolRow: React.FC<React.PropsWithChildren<PrizePoolRowProps>> = ({ totalAmount, ...props }) => {
  const { t } = useTranslation()
  const { token, displayedDecimals } = useConfig()

  return (
    <Row {...props}>
      <Text fontWeight={700}>{t('Prize Pool')}:</Text>
      {/* <Text fontWeight={700}>8.4337 OP</Text> */}
      <Text fontWeight={700}>{`${getPrizePoolAmount(totalAmount, 2)} FTM`}</Text>
    </Row>
  )
}

// Payout Row
interface PayoutRowProps extends FlexProps {
  positionLabel: string
  multiplier: number
  amount: number
}

export const PayoutRow: React.FC<React.PropsWithChildren<PayoutRowProps>> = ({
  positionLabel,
  multiplier,
  amount,
  ...props
}) => {
  const { t } = useTranslation()
  const formattedMultiplier = `${multiplier.toLocaleString(undefined, { maximumFractionDigits: 2 })}x`
  const { token, displayedDecimals } = useConfig()

  return (
    <Row height="18px" {...props}>
      <Text color="white60" textTransform="uppercase">
        {positionLabel}
      </Text>
      <Flex alignItems="center">
        <Text fontSize="14px" fontWeight={700}>
          {t('%multiplier% Payout', { multiplier: formattedMultiplier })}
        </Text>
        <Text mx="4px">|</Text>
        <Text fontSize="14px" fontWeight={700}>{`${formatBnb(amount, 2)} FTM`}</Text>
      </Flex>
    </Row>
  )
}

interface LockPriceRowProps extends FlexProps {
  lockPrice: NodeRound['lockPrice']
}

export const LockPriceRow: React.FC<React.PropsWithChildren<LockPriceRowProps>> = ({ lockPrice, ...props }) => {
  const { t } = useTranslation()
  const { displayedDecimals, minPriceUsdDisplayed } = useConfig()

  return (
    <Row {...props}>
      <Text fontSize="14px">{t('Locked Price')}:</Text>
      <Text fontSize="14px">{formatUsdv2(lockPrice, minPriceUsdDisplayed, displayedDecimals)}</Text>
    </Row>
  )
}

// RoundResultBox
interface RoundResultBoxProps {
  betPosition?: BetPosition
  isNext?: boolean
  isLive?: boolean
  hasEntered?: boolean
  style?: any
}

const getBackgroundColor = ({
  theme,
  betPosition,
  isNext,
  isLive,
  hasEntered,
}: RoundResultBoxProps & { theme: DefaultTheme }) => {
  if (isNext) {
    return 'linear-gradient(196.44deg, #31FFA8, #FF00F5, #3DFF44)'
  }

  if (isLive) {
    return '#FF4D4F'
  }

  if (hasEntered) {
    return theme.colors.secondary
  }

  switch (betPosition) {
    case BetPosition.BULL:
      return theme.colors.success
    case BetPosition.BEAR:
      return theme.colors.failure
    case BetPosition.HOUSE:
      return theme.colors.textDisabled
    default:
      return '#FFFFFF1A'
  }
}

const Background = styled(Box)<RoundResultBoxProps>`
  background: ${getBackgroundColor};
  border-radius: 24px;
  padding: 1px;
`

const StyledRoundResultBox = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  background: ${({ theme }) => theme.colors.backgroundAlt};
  border-radius: 24px;
  padding: 24px;
  height: 188px;
`

export const RoundResultBox: React.FC<React.PropsWithChildren<RoundResultBoxProps>> = ({
  isNext = false,
  hasEntered = false,
  isLive = false,
  children,
  style,
  ...props
}) => {
  return (
    <Background isNext={isNext} hasEntered={hasEntered} isLive={isLive} {...props}>
      <StyledRoundResultBox style={style}>{children}</StyledRoundResultBox>
    </Background>
  )
}

interface RoundPriceProps {
  lockPrice: BigNumber
  closePrice: BigNumber
}

export const RoundPrice: React.FC<React.PropsWithChildren<RoundPriceProps>> = ({ lockPrice, closePrice }) => {
  const { displayedDecimals, minPriceUsdDisplayed } = useConfig()
  const betPosition = getRoundPosition(lockPrice, closePrice)
  const priceDifference = getPriceDifference(closePrice, lockPrice)

  const textColor = useMemo(() => {
    switch (betPosition) {
      case BetPosition.BULL:
        return 'success'
      case BetPosition.BEAR:
        return 'failure'
      case BetPosition.HOUSE:
      default:
        return 'textDisabled'
    }
  }, [betPosition])

  return (
    <Flex alignItems="center" justifyContent="space-between" mb="16px" height="36px">
      {closePrice ? (
        <Text color={textColor} fontWeight={700} fontSize="20px">
          {formatUsdv2(closePrice, minPriceUsdDisplayed, displayedDecimals)}
        </Text>
      ) : (
        <Skeleton height="34px" my="1px" />
      )}
      <PositionTag betPosition={betPosition} height="100%">
        {formatUsdv2(priceDifference.abs(), minPriceUsdDisplayed, displayedDecimals)}
      </PositionTag>
    </Flex>
  )
}

/**
 * TODO: Remove
 *
 * This is a temporary function until we consolidate the data coming from the graph versus the node
 */
interface PrizePoolHistoryRowProps extends FlexProps {
  totalAmount: number
}

const getPrizePoolAmountHistory = (totalAmount: PrizePoolHistoryRowProps['totalAmount'], displayedDecimals: number) => {
  if (!totalAmount) {
    return '0'
  }

  return formatBnb(totalAmount, displayedDecimals)
}

export const PrizePoolHistoryRow: React.FC<React.PropsWithChildren<PrizePoolHistoryRowProps>> = ({
  totalAmount,
  ...props
}) => {
  const { t } = useTranslation()
  const { token, displayedDecimals } = useConfig()

  return (
    <Row {...props}>
      <Text color="white60">{t('Prize Pool')}</Text>
      <Text fontWeight={700} style={{ color: '#03F5AE' }}>{`${getPrizePoolAmountHistory(totalAmount, 2)} FTM`}</Text>
    </Row>
  )
}

interface LockPriceHistoryRowProps extends FlexProps {
  lockPrice: Round['lockPrice']
}

export const LockPriceHistoryRow: React.FC<React.PropsWithChildren<LockPriceHistoryRowProps>> = ({
  lockPrice,
  ...props
}) => {
  const { t } = useTranslation()
  const { displayedDecimals } = useConfig()

  return (
    <Row {...props}>
      <Text color="white60">{t('Locked Price')}</Text>
      <Text fontWeight={700}>{formatUsd(lockPrice, displayedDecimals)}</Text>
    </Row>
  )
}
/**
 * END TEMPORARY COMPONENTS
 */
